import React from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import { FieldValues, useForm, useFormContext } from 'react-hook-form';
import { useSelector } from 'react-redux';

import { getScheduledFlightsByFlightNumber, getScheduledFlightsByRoute } from 'api/flightApi/searchForFlight';
import { getUniqueFlightAPIKey } from 'components/Pages/Admin/GuestPortal/Flights/AddFlights/utils';
import { ACCOUNT_TYPE_AGENT } from 'constants/index';
import { EOrganizedBy, ETripEvent, IAirport, IFlightAPIEventData, ITripEvent } from 'detailsv2/structure';
import { PERMISSION_GUEST_PORTAL, useIsSubscriptionAllowed } from 'hooks/useIsSubscriptionAllowed';
import { itineraryScheduleTypeSelector } from 'store/itinerariesItem/selectors';
import { reverseFlightDateValues } from 'utils/common';
import { getCurrentDateString } from 'utils/date';
import { camelizeKeys } from 'utils/humps';

import { EFlightSearchTypes } from '../../../../../constants';
import { FlightAPIOptions } from './FlightAPIOption/types';
import { FlightTab, ISearchBy } from './types';
import { flightAPIValidationSchema, flightManualValidationSchema } from './validation';

export function useFlightModalContent(tripEvent: ITripEvent<ETripEvent.Flight>) {
  const organizedBy = useSelector(itineraryScheduleTypeSelector);
  const [activeTab, _setActiveTab] = React.useState<FlightTab>(() => {
    const searchType = {
      [EFlightSearchTypes.FLIGHT_NUMBER]: FlightTab.NUMBER_SEARCH,
      [EFlightSearchTypes.ROUTE]: FlightTab.ROUTE_SEARCH,
      [EFlightSearchTypes.MANUAL]: FlightTab.MANUAL,
    };

    if (searchType[tripEvent?.data?.fsSearchType]) return searchType[tripEvent?.data?.fsSearchType];

    if (tripEvent.id && !tripEvent?.data?.fromFsService) return FlightTab.MANUAL;

    return FlightTab.NUMBER_SEARCH;
  });

  const setActiveTab = (tab: FlightTab) => {
    if (tab === FlightTab.MANUAL) {
      setTimeout(() => {
        methods.setValue('data.departureTime', '');
        methods.setValue('data.departureFrom', '');
        methods.setValue('data.arrivalTime', '');
        methods.setValue('data.arrivalTo', '');
        methods.setValue('data.airline', '');
        methods.setValue('data.flightNumber', '');
      });
    }

    _setActiveTab(tab);
  };
  const isFlightAPI = activeTab !== FlightTab.MANUAL;
  const data = tripEvent.data as IFlightAPIEventData;
  const initialData = (() => {
    if (activeTab === FlightTab.MANUAL) return {};
    if (!data?.flightNumber) return {};

    const key = getUniqueFlightAPIKey(data);
    return { [key]: tripEvent };
  })();
  // @ts-expect-error
  const [activeItems, setActiveItems] = React.useState<FlightAPIOptions>(initialData);

  const methods = useForm({
    defaultValues: {
      ...(tripEvent.data.isIDL ? reverseFlightDateValues(tripEvent, true) : tripEvent),
      attachedPage: tripEvent?.attachedPages?.[0],
    } as FieldValues,
    resolver: yupResolver(
      isFlightAPI ? flightAPIValidationSchema(organizedBy) : flightManualValidationSchema(organizedBy),
    ),
    mode: 'onTouched',
  });

  const isDisabled =
    activeTab === FlightTab.MANUAL
      ? Object.keys(methods.formState.errors).length !== 0
      : Object.keys(methods.formState.errors).length !== 0 || organizedBy === EOrganizedBy.Day;

  React.useEffect(() => {
    methods.setValue('data.fromFsService', isFlightAPI);
    const searchType = {
      [FlightTab.NUMBER_SEARCH]: EFlightSearchTypes.FLIGHT_NUMBER,
      [FlightTab.ROUTE_SEARCH]: EFlightSearchTypes.ROUTE,
      [FlightTab.MANUAL]: EFlightSearchTypes.MANUAL,
    };
    methods.setValue('data.fsSearchType', searchType[activeTab] || EFlightSearchTypes.MANUAL);

    if (isFlightAPI) {
      methods.setValue(
        'data.flightDate',
        // @ts-expect-error
        tripEvent?.data?.flightDate || tripEvent?.datesRange?.start || getCurrentDateString(),
      );
    }
  }, [activeTab]);

  return {
    isDisabled,
    activeTab,
    setActiveTab,
    activeItems,
    setActiveItems,
    organizedBy,
    methods,
  };
}

export const useFlightSearch = ({
  hasSelectedValues,
  tripEvent,
  setSearchResults,
  setActiveItems,
  setShowSelect,
  organizedBy,
}: ISearchBy) => {
  const { getValues, setValue } = useFormContext();
  const departureDate = getValues('data.flightDate');
  const data = tripEvent.data as IFlightAPIEventData;
  const [loading, setLoading] = React.useState(false);
  const [flightNumber, setFlightNumber] = React.useState<string>(tripEvent.data?.flightNumber || '');
  const [airlineFsCode, setAirlineFsCode] = React.useState<string | null | undefined>(data?.airline?.fsCode || '');
  const [arrivalAirportFsCode, setArrivalAirportFsCode] = React.useState<string | null>(
    data?.arrivalAirport?.fsCode || '',
  );
  const [departureAirportFsCode, setDepartureAirportFsCode] = React.useState<string | null>(
    data?.departureAirport?.fsCode || '',
  );

  const isOrganizedByDay = organizedBy === EOrganizedBy.Day;
  const isDisabledAirportSearch = !arrivalAirportFsCode || !departureAirportFsCode || isOrganizedByDay;
  const isDisabledAirlineSearch = !flightNumber || !airlineFsCode || isOrganizedByDay;
  const isTier2Subscription = useIsSubscriptionAllowed(ACCOUNT_TYPE_AGENT, PERMISSION_GUEST_PORTAL);

  const onChangeFlightNumber = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFlightNumber(e.currentTarget.value);
  };

  const getFlightSchedules = async () => {
    setActiveItems({});
    setLoading(true);
    const result = await getScheduledFlightsByFlightNumber(airlineFsCode!, flightNumber, departureDate, setLoading);
    setSearchResults(camelizeKeys(result?.data));
    // setLoading(false);
    setValue('data.fromFsService', true);
    setShowSelect(true);
    // setSearchResults(fakeApiFlightData);
  };

  const getFlightSchedulesByRoute = async () => {
    setActiveItems({});
    setLoading(true);
    const result = await getScheduledFlightsByRoute(
      departureAirportFsCode!,
      arrivalAirportFsCode!,
      departureDate,
      setLoading,
    );
    setSearchResults(camelizeKeys(result?.data));
    // setLoading(false);
    setValue('data.fromFsService', true);
    setShowSelect(true);
    // setSearchResults(fakeApiFlightData);
  };

  const clearSearchResults = () => {
    setSearchResults(null);
    setActiveItems({});
  };

  React.useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      if (event.key === 'Enter' && !hasSelectedValues) {
        if (!isDisabledAirportSearch) getFlightSchedulesByRoute();
        if (!isDisabledAirlineSearch) getFlightSchedules();
      }
    };

    document.addEventListener('keydown', handleKeyPress);

    return () => document.removeEventListener('keydown', handleKeyPress);
  }, [isDisabledAirportSearch, isDisabledAirlineSearch, getFlightSchedules, getFlightSchedulesByRoute]);

  return {
    getFlightSchedules,
    getFlightSchedulesByRoute,
    getAirportDefaultValue,
    loading,
    flightNumber,
    setAirlineFsCode,
    onChangeFlightNumber,
    isDisabledAirlineSearch,
    isDisabledAirportSearch,
    setArrivalAirportFsCode,
    setDepartureAirportFsCode,
    clearSearchResults,
    isTier2Subscription,
    isOrganizedByDay,
  };
};

function getAirportDefaultValue(airport?: IAirport) {
  if (!airport) return;

  return { ...airport, name: `${airport.name} (${airport.fsCode})` };
}
